import { apiSlice } from './http';
import { Tags } from '../enum/tags';

// Enums
export enum TipoDivergenciaEnum {
  PULO_NUMERACAO = 'PULO_NUMERACAO',
  EXCLUSIVO_DOC001 = 'EXCLUSIVO_DOC001',
  EXCLUSIVO_VALIDATOR = 'EXCLUSIVO_VALIDATOR',
  STATUS_DIVERGENTE = 'STATUS_DIVERGENTE',
  // GFT Livro Fiscal types
  PULO_NUMERACAO_GFT = 'PULO_NUMERACAO_GFT',
  EXCLUSIVO_GFT_LIVRO = 'EXCLUSIVO_GFT_LIVRO',
  EXCLUSIVO_DOC001_GFT = 'EXCLUSIVO_DOC001_GFT',
  STATUS_DIVERGENTE_GFT = 'STATUS_DIVERGENTE_GFT',
  STATUS_DIVERGENTE_GFT_VALIDATOR = 'STATUS_DIVERGENTE_GFT_VALIDATOR'
}

// API Response Types
export interface ConciliacaoFilterDto {
  startDate: string;
  endDate: string;
  incluirPulos?: boolean;
  incluirStatus?: boolean;
  incluirGftLivro?: boolean;
  tipos?: TipoDivergenciaEnum[];
  inutilizadoLogix?: boolean;
  statusDoc001?: string;
  statusValidator?: string;
  statusGft?: string;
  page?: number;
  limit?: number;
}

export interface PuloNumeracaoDto {
  loja: string;
  caixa: string;
  data: string;
  cupomAnterior: number;
  proximoCupom: number;
  cupomsFaltantes: number[];
  tamanhoPulo: number;
  existeNoValidator: boolean[];
}

export interface ExclusivoDoc001Dto {
  loja: string;
  caixa: string;
  controleVenda: string;
  data: string;
  cupom: number;
  situacao: string;
  situacao2: string;
  statusCalculado: string;
}

export interface ExclusivoValidatorDto {
  loja: string;
  caixa: string;
  cupom: number;
  data: string;
  status: string;
}

export interface StatusDivergenteDto {
  loja: string;
  caixa: string;
  cupom: number;
  data: string;
  statusDoc001: string;
  statusValidator: string;
  controleVenda: string;
}

export interface ConciliacaoResumoDto {
  totalDoc001: number;
  totalValidator: number;
  totalDivergencias: number;
  statusConciliacao: 'SUCESSO' | 'PARCIAL' | 'ERRO';
  totalGftLivro?: number;
  estatisticas: {
    pulosNumeracao: number;
    exclusivosDoc001: number;
    exclusivosValidator: number;
    statusDivergentes: number;
    divergenciasCriticas: number;
    divergenciasTemporais: number;
    divergenciasInvestigacao: number;
    // GFT-specific statistics
    pulosNumeracaoGft?: number;
    exclusivosGftLivro?: number;
    statusDivergentesGft?: number;
  };
}

// BREAKING CHANGE: New unified divergence types

export interface DivergenciaUnificadaDto {
  id: string;
  tipo: TipoDivergenciaEnum;
  loja: string;
  caixa: string;
  cupom: number;
  data: string;
  detectadoEm: string;

  // Campos específicos para PULO_NUMERACAO
  cupomAnterior?: number;
  proximoCupom?: number;
  tamanhoPulo?: number;
  existeNoValidator?: boolean;
  statusValidatorPulos?: string | null;
  inutilizadoLogix?: boolean;
  statusSimplificado?: string;
  cnpjLoja?: string;

  // Campos específicos para EXCLUSIVO_DOC001
  statusDoc001?: string;

  // Campos específicos para EXCLUSIVO_VALIDATOR
  statusValidator?: string;
  situacao?: string;
  status?: string;
  motivo?: 'NAO_ENVIADO_VALIDATOR' | 'ERRO_INTEGRACAO' | 'CUPOM_CANCELADO' | 'NAO_PROCESSADO_DOC001' | 'ERRO_GERACAO' | 'CUPOM_FANTASMA';

  // Campos específicos para STATUS_DIVERGENTE
  statusDoc001Calculado?: string;
  situacaoRaw?: string;
  situacao2Raw?: string;
  tipoDiscrepancia?: 'STATUS_DIFERENTE' | 'DATA_DIFERENTE' | 'STATUS_DESCONHECIDO';
  detalhes?: string;
  gravidade?: 'CRITICA' | 'TEMPORAL' | 'INVESTIGACAO';

  // Campos específicos para GFT Livro Fiscal
  statusGftLivro?: string;
  existeGftLivro?: boolean;
  origemDivergencia?: 'DOC001_VALIDATOR' | 'DOC001_GFT';
}

export interface DivergenciasUnificadaResponseDto {
  divergencias: DivergenciaUnificadaDto[];
  resumoPorTipo: Record<TipoDivergenciaEnum, { total: number }>;
}

// DEPRECATED: Legacy interface - will be replaced by DivergenciasUnificadaResponseDto
export interface ConciliacaoDivergenciasDto {
  pulosNumeracao: PuloNumeracaoDto[];
  apenasDoc001: ExclusivoDoc001Dto[];
  apenasValidator: ExclusivoValidatorDto[];
  statusDivergentes: StatusDivergenteDto[];
}

export interface PaginacaoDto {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
  hasNext: boolean;
  hasPrev: boolean;
}

export interface ConciliacaoResponseDto {
  periodo: {
    dataInicio: string;
    dataFim: string;
  };
  resumo: ConciliacaoResumoDto;
  // BREAKING CHANGE: divergencias now uses unified structure
  divergencias: DivergenciasUnificadaResponseDto;
  paginacao: PaginacaoDto;
  executadoEm?: string;
  tempoProcessamento?: number;
  async?: boolean;
  jobId?: string;
  metadata?: {
    gftIncluded: boolean;
    gftError?: {
      code: string;
      message: string;
    };
  };
}

export interface AsyncJobStatusDto {
  id: string;
  status: 'pending' | 'processing' | 'completed' | 'failed' | 'cancelled';
  progress?: number;
  result?: ConciliacaoResponseDto;
  error?: string;
  estimatedCompletion?: string;
}

// Using main Tags enum for cache invalidation

export const conciliacaoFiscalApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    // Execute reconciliation
    executarConciliacao: builder.query<ConciliacaoResponseDto, ConciliacaoFilterDto>({
      query: (params) => ({
        url: '/conciliacao-fiscal',
        method: 'GET',
        params: {
          startDate: params.startDate,
          endDate: params.endDate,
          incluirPulos: params.incluirPulos,
          incluirStatus: params.incluirStatus,
          incluirGftLivro: params.incluirGftLivro,
          tipos: params.tipos,
          inutilizadoLogix: params.inutilizadoLogix,
          statusDoc001: params.statusDoc001,
          statusValidator: params.statusValidator,
          statusGft: params.statusGft,
          page: params.page,
          limit: params.limit,
        },
      }),
      providesTags: [Tags.CONCILIACAO_FISCAL],
    }),

    // Check async job status
    consultarStatusJob: builder.query<AsyncJobStatusDto, string>({
      query: (jobId) => `/conciliacao-fiscal/status/${jobId}`,
      providesTags: (_result, _error, jobId) => [
        { type: Tags.ASYNC_JOB, id: jobId }
      ],
    }),

    // Cancel async job
    cancelarJob: builder.mutation<void, string>({
      query: (jobId) => ({
        url: `/conciliacao-fiscal/cancel/${jobId}`,
        method: 'POST',
      }),
      invalidatesTags: (_result, _error, jobId) => [
        { type: Tags.ASYNC_JOB, id: jobId }
      ],
    }),

    // Clear cache
    limparCache: builder.mutation<void, { startDate?: string; endDate?: string }>({
      query: (params) => ({
        url: '/conciliacao-fiscal/cache',
        method: 'DELETE',
        params,
      }),
      invalidatesTags: [Tags.CONCILIACAO_FISCAL],
    }),
  }),
  overrideExisting: false,
});

export const {
  useLazyExecutarConciliacaoQuery,
  useConsultarStatusJobQuery,
  useCancelarJobMutation,
  useLimparCacheMutation,
} = conciliacaoFiscalApi;